import { ChangeEvent, useState } from 'react'
import { Formik, FormikHelpers, Field } from 'formik'
import { useIntl } from 'react-intl'

import type { UserCredentials, User } from '@racepointenergy/swc/scs/types'
import { createClients } from '@racepointenergy/swc/scs/create-clients'
import { createUsers2faCheck } from '@racepointenergy/swc/scs/create-users-2fa-check'
import routes from 'app/routes'
import { useAppDispatch } from 'app/hooks'
import { updateAuthStatusThunk } from 'features/auth/login/slice'
import { storeAuthorizedUser } from 'app/services/LocalStorage'

import {
  ForgotPasswordWrapper,
  ForgotPasswordLink,
  StyledTermsAndPolicyText,
  SubmitLoginWrapper,
  StyledLoginForm,
  StyledTermsLink,
} from './styles'
import type { LoginFormValues, LoginFormProps } from '../../types'
import { loginMessages } from '../../messages'

import CommonButton from 'components/Button/CommonButton'
import { EmailField, PasswordField } from 'components/Fields'
import { LoaderOverlay } from 'components/Loader'
import { LOGIN_ERRORS } from '../../api'

export default function LoginForm(props: LoginFormProps) {
  const { setLoginValues, setError } = props

  const dispatch = useAppDispatch()
  const { formatMessage } = useIntl()
  const [dispatchCreateClient] = createClients.useMutation()
  const [dispatchCreateUsers2faCheck] = createUsers2faCheck.useMutation()
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = async (
    values: LoginFormValues,
    formikHelpers: FormikHelpers<LoginFormValues>
  ) => {
    setSubmitted(true)
    const clientId = (await dispatchCreateClient({}).unwrap()) || ''
    const { email, password } = values
    dispatchCreateUsers2faCheck({
      clientId,
      email: email || '',
      password: password || '',
    })
      .unwrap()
      .then((result) => {
        // TODO: update SWC
        /// @ts-ignore
        if (result.error?.code === LOGIN_ERRORS.WRONG_CREDENTIALS) {
          setError({
            /// @ts-ignore
            code: result.error?.code,
            header: formatMessage(loginMessages.errorHeader),
            message: formatMessage(loginMessages.wrongCredentials),
          })
          setSubmitted(false)
          return
        }

        if (
          result.twoFactor.length &&
          result.twoFactor[0].enabled === 'Enabled'
        ) {
          setLoginValues({
            clientId,
            userId: result.userId,
            email,
            password,
            twoFactor: { type: result.twoFactor[0].type },
          })
          setSubmitted(false)
          return
        }
        storeAuthorizedUser(result as UserCredentials & User)
        dispatch(updateAuthStatusThunk())
      })
      .catch((error) => {
        console.log(error)
        if (error.data?.error?.code === LOGIN_ERRORS.WRONG_CREDENTIALS) {
          setError({
            code: error.data?.error?.code,
            header: formatMessage(loginMessages.errorHeader),
            message: formatMessage(loginMessages.wrongCredentials),
          })
        }
        /// @ts-ignore
        else if (error.data?.error?.code === LOGIN_ERRORS.DOMAIN_ERROR) {
          setError({
            /// @ts-ignore
            code: error.data?.error?.code,
            header: formatMessage(loginMessages.domainErrorHeader),
            message: formatMessage(loginMessages.domainError),
          })
          setSubmitted(false)
          return
        }
        setSubmitted(false)
      })
  }

  type ChangeEventHandler = (e: ChangeEvent<any>) => void

  const handleLoginInputsChange = (
    e: ChangeEvent<any>,
    fieldHandleChange: ChangeEventHandler
  ) => {
    fieldHandleChange(e)
  }

  const initialLoginValues: LoginFormValues = {
    email: '',
    password: '',
    clientId: '',
    twoFactor: [],
  }

  const terms = (
    <StyledTermsLink
      href="https://www.savant.com/terms-and-conditions"
      target="_blank"
    >
      {formatMessage(loginMessages.termsOfService)}
    </StyledTermsLink>
  )

  const privacy = (
    <StyledTermsLink
      href="https://www.savant.com/privacy-policy"
      target="_blank"
    >
      {formatMessage(loginMessages.privacyPolicy)}
    </StyledTermsLink>
  )

  return (
    <Formik initialValues={initialLoginValues} onSubmit={handleSubmit}>
      {(formikProps) => (
        <StyledLoginForm>
          {submitted && <LoaderOverlay isFullScreen />}
          <EmailField
            name="email"
            autoComplete="email"
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              handleLoginInputsChange(e, formikProps.handleChange)
            }
            invalid={formikProps.errors.email}
          />

          <PasswordField
            name="password"
            autoComplete="current-password"
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              handleLoginInputsChange(e, formikProps.handleChange)
            }
            invalid={formikProps.errors.password}
          />

          <Field id="cliendId" name="clientId" type="hidden" />

          <ForgotPasswordWrapper>
            <ForgotPasswordLink to={routes.auth.forgotPassword}>
              {formatMessage(loginMessages.forgotPassword)}
            </ForgotPasswordLink>
          </ForgotPasswordWrapper>

          <SubmitLoginWrapper>
            <CommonButton
              variant="primary"
              type="submit"
              disabled={
                formikProps.isSubmitting ||
                !formikProps.isValid ||
                !formikProps.values.email ||
                !formikProps.values.password
              }
              data-testid="sign-in-submit-btn"
            >
              {formatMessage(loginMessages.loginButton)}
            </CommonButton>
          </SubmitLoginWrapper>
          <StyledTermsAndPolicyText>
            {formatMessage(loginMessages.termsAndPolicy, { terms, privacy })}
          </StyledTermsAndPolicyText>
        </StyledLoginForm>
      )}
    </Formik>
  )
}
